import { FC, MouseEvent } from "react";
import {
  COLOR_PRIMARY_MAIN,
  COLOR_GREY_11,
  COLOR_BLACK,
} from "shared/theme/colors";
import { CheckIcon } from "shared/theme/icons/CheckIcon";
import { ExpandMoreIcon } from "shared/theme/icons/ExpandMoreIcon";
import { ExpandLessIcon } from "../../theme/icons/ExpandLessIcon";
import ClearButton from "../ClearButton";
import CountIndicator from "../CountIndicator";
import FlexCenter from "../Layout/components/FlexCenter";
import {
  StyledButton,
  StyledFooterWrapper,
  StyledMenu,
  StyledMenuItem,
  StyledTypography,
} from "./styled";

export type FilterOption = { value: string; label: string };

export type FilterProps = {
  title: string;
  anchorEl: null | HTMLElement;
  options: FilterOption[];
  selectedValue: FilterOption[];
  open: boolean;
  handleOpenMenu: (event: MouseEvent<HTMLButtonElement>) => void;
  handleChangeValue: (
    event: MouseEvent<HTMLElement>,
    value: FilterOption,
  ) => void;
  handleClose: () => void;
  disabled?: boolean;
  onClearSelectedValues: () => void;
  isSmallButton?: boolean;
  sortAlphabetically?: boolean;
};

const Filter: FC<FilterProps> = ({
  options,
  open,
  handleChangeValue,
  handleClose,
  handleOpenMenu,
  title,
  selectedValue,
  anchorEl,
  disabled,
  onClearSelectedValues,
  isSmallButton = false,
  sortAlphabetically,
}) => {
  const getVisibility = (value: string): "visible" | "hidden" =>
    selectedValue?.some((selected) => selected.value === value)
      ? "visible"
      : "hidden";

  const sortedOptions = sortAlphabetically
    ? [...options]
        .filter((item) => item.value)
        .sort((a, b) => a.value.localeCompare(b.value))
    : options;

  return (
    <>
      <StyledButton
        id="filter-button"
        aria-controls={open ? "filter-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disableRipple
        isActive={open}
        isSmallButton={isSmallButton}
        onClick={handleOpenMenu}
        disabled={disabled}
        endIcon={
          <FlexCenter columnGap={isSmallButton ? "4px" : "0.5rem"}>
            {Boolean(selectedValue?.length) ? (
              <CountIndicator
                count={selectedValue?.length}
                isSmallIndicator={isSmallButton}
              />
            ) : null}
            {open ? (
              <ExpandLessIcon
                size={isSmallButton ? 16 : 24}
                color={isSmallButton ? COLOR_BLACK : COLOR_GREY_11}
              />
            ) : (
              <ExpandMoreIcon
                size={isSmallButton ? 16 : 24}
                color={isSmallButton ? COLOR_BLACK : COLOR_GREY_11}
              />
            )}
          </FlexCenter>
        }
      >
        <StyledTypography variant={"bodySMedium"} color={COLOR_BLACK}>
          {title}
        </StyledTypography>
      </StyledButton>
      <StyledMenu
        id="filter-menu"
        aria-labelledby="filter-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {sortedOptions.map((option) => (
          <StyledMenuItem
            key={option.value}
            onClick={(e) => handleChangeValue(e, option)}
          >
            <FlexCenter
              sx={{
                visibility: getVisibility(option.value),
              }}
            >
              <CheckIcon color={COLOR_PRIMARY_MAIN} />
            </FlexCenter>
            <StyledTypography>{option.label}</StyledTypography>
          </StyledMenuItem>
        ))}
        <StyledFooterWrapper>
          <ClearButton onClearFilters={onClearSelectedValues} />
        </StyledFooterWrapper>
      </StyledMenu>
    </>
  );
};

export default Filter;
